import React from "react";
import { useTranslation } from "react-i18next";
import { Group } from "../../features/members/models/Group";
import { Member } from "../../features/members/models/Member";

export interface MaterialAudience {
  everyone: boolean;
  memberIds: string[];
  groupIds: string[];
}

interface MaterialAudiencePickerProps {
  members: Member[];
  groups: Group[];
  value: MaterialAudience;
  disabled: boolean;
  onChange: (value: MaterialAudience) => void;
}

/** Choose who sees a material: everyone (default) or selected members / groups. */
const MaterialAudiencePicker: React.FC<MaterialAudiencePickerProps> = ({
  members,
  groups,
  value,
  disabled,
  onChange,
}) => {
  const { t } = useTranslation();

  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((item) => item !== id) : [...list, id];

  return (
    <div>
      <p className="mb-1 text-sm text-grey-20 dark:text-grey-80">
        {t("materials.audienceLabel", "Who can see it")}
      </p>
      <div className="flex gap-4">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-grey-20 dark:text-grey-80">
          <input
            type="radio"
            name="material-audience"
            checked={value.everyone}
            onChange={() => onChange({ everyone: true, memberIds: [], groupIds: [] })}
            disabled={disabled}
          />
          {t("materials.audienceEveryone", "Everyone")}
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-grey-20 dark:text-grey-80">
          <input
            type="radio"
            name="material-audience"
            checked={!value.everyone}
            onChange={() => onChange({ ...value, everyone: false })}
            disabled={disabled}
          />
          {t("materials.audienceSelected", "Specific members or groups")}
        </label>
      </div>

      {!value.everyone && (
        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="max-h-40 overflow-y-auto rounded-lg border border-grey-90 p-2 dark:border-grey-50">
            <p className="mb-1 text-xs font-semibold uppercase text-grey-40 dark:text-grey-80">
              {t("materials.audienceGroups", "Groups")}
            </p>
            {groups.length === 0 && (
              <p className="text-xs text-grey-40 dark:text-grey-80">
                {t("materials.audienceNoGroups", "No groups yet.")}
              </p>
            )}
            {groups.map((group) => (
              <label
                key={group.id}
                className="flex cursor-pointer items-center gap-2 py-0.5 text-sm text-grey-20 dark:text-grey-80"
              >
                <input
                  type="checkbox"
                  checked={value.groupIds.includes(group.id)}
                  onChange={() => onChange({ ...value, groupIds: toggle(value.groupIds, group.id) })}
                  disabled={disabled}
                />
                {group.name}
              </label>
            ))}
          </div>
          <div className="max-h-40 overflow-y-auto rounded-lg border border-grey-90 p-2 dark:border-grey-50">
            <p className="mb-1 text-xs font-semibold uppercase text-grey-40 dark:text-grey-80">
              {t("materials.audienceMembers", "Members")}
            </p>
            {members.map((member) => (
              <label
                key={member.id}
                className="flex cursor-pointer items-center gap-2 py-0.5 text-sm text-grey-20 dark:text-grey-80"
              >
                <input
                  type="checkbox"
                  checked={value.memberIds.includes(member.id)}
                  onChange={() =>
                    onChange({ ...value, memberIds: toggle(value.memberIds, member.id) })
                  }
                  disabled={disabled}
                />
                {member.firstName} {member.lastName}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialAudiencePicker;
